export interface StorageModeStatus {
  currentMode: 'localStorage';
  isLocked: boolean;
  persistenceLevel: 'permanent';
  lastVerified: string;
  healthStatus: 'healthy' | 'warning' | 'error';
  capacity: {
    maxStudents: number;
    currentUsage: string;
  };
}

export class StorageModeManager {
  private static instance: StorageModeManager;
  private readonly STATUS_KEY = 'nalc_storage_status';
  private isMonitoring = false;

  static getInstance(): StorageModeManager {
    if (!StorageModeManager.instance) {
      StorageModeManager.instance = new StorageModeManager();
    }
    return StorageModeManager.instance;
  }

  async initializeStorageMode(): Promise<StorageModeStatus> {
    console.log('🔧 StorageModeManager: Initializing localStorage-only mode...');
    
    // Always force localStorage mode
    this.lockStorageMode('localStorage');
    this.startHealthMonitoring();
    
    return await this.getStorageStatus();
  }

  private lockStorageMode(mode: 'localStorage'): void {
    const lockData = {
      mode,
      timestamp: new Date().toISOString(),
      locked: true
    };
    
    localStorage.setItem('nalc_storage_mode_lock', JSON.stringify(lockData));
    sessionStorage.setItem('nalc_storage_mode_lock', JSON.stringify(lockData));
    
    console.log(`🔒 Storage mode locked to: ${mode}`);
  }

  private startHealthMonitoring(): void {
    if (this.isMonitoring) return;
    
    this.isMonitoring = true;
    
    // Check storage health every 30 seconds
    setInterval(async () => {
      await this.checkStorageHealth();
    }, 30000);
    
    // Also check on page visibility change
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.checkStorageHealth();
      }
    });
    
    console.log('👁️ Storage health monitoring started');
  }

  private async checkStorageHealth(): Promise<void> {
    try {
      const status = await this.getStorageStatus();
      
      // Test localStorage access
      const testKey = 'health_check_test';
      const testData = { timestamp: Date.now() };
      
      localStorage.setItem(testKey, JSON.stringify(testData));
      const retrieved = localStorage.getItem(testKey);
      localStorage.removeItem(testKey);
      
      if (!retrieved) {
        throw new Error('localStorage health check failed');
      }
      
      const parsedData = JSON.parse(retrieved);
      if (!parsedData.timestamp || parsedData.timestamp !== testData.timestamp) {
        throw new Error('localStorage data integrity check failed');
      }
      
      // Get storage capacity info
      const { universalStorage } = await import('./universalStorage');
      const capacityInfo = await universalStorage.estimateStorageCapacity();
      
      // Update health status
      await this.saveStorageStatus({
        ...status,
        healthStatus: 'healthy',
        lastVerified: new Date().toISOString(),
        capacity: {
          maxStudents: capacityInfo.maxCapacity,
          currentUsage: capacityInfo.storageUsed
        }
      });
    } catch (error) {
      console.error('Storage health check failed:', error);
      
      const status = await this.getStorageStatus();
      await this.saveStorageStatus({
        ...status,
        healthStatus: 'error',
        lastVerified: new Date().toISOString()
      });
    }
  }

  async getStorageStatus(): Promise<StorageModeStatus> {
    try {
      const stored = localStorage.getItem(this.STATUS_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          currentMode: 'localStorage',
          isLocked: true,
          persistenceLevel: 'permanent',
          lastVerified: parsed.lastVerified || new Date().toISOString(),
          healthStatus: parsed.healthStatus || 'healthy',
          capacity: parsed.capacity || { maxStudents: 0, currentUsage: 'Unknown' }
        };
      }
    } catch (error) {
      console.error('Error reading storage status:', error);
    }
    
    // Default status - always localStorage
    return {
      currentMode: 'localStorage',
      isLocked: true,
      persistenceLevel: 'permanent',
      lastVerified: new Date().toISOString(),
      healthStatus: 'healthy',
      capacity: { maxStudents: 0, currentUsage: 'Unknown' }
    };
  }

  private async saveStorageStatus(status: StorageModeStatus): Promise<void> {
    try {
      localStorage.setItem(this.STATUS_KEY, JSON.stringify(status));
      sessionStorage.setItem(this.STATUS_KEY, JSON.stringify(status));
    } catch (error) {
      console.error('Error saving storage status:', error);
    }
  }

  // Simplified interface - no file system methods
  isFileSystemModeLocked(): boolean {
    return false; // Never true since we only use localStorage
  }

  async enableFileSystemMode(): Promise<boolean> {
    return false; // Not available
  }

  async getStorageCapacity(): Promise<{ maxStudents: number; currentUsage: string }> {
    try {
      const { universalStorage } = await import('./universalStorage');
      const capacityInfo = await universalStorage.estimateStorageCapacity();
      return {
        maxStudents: capacityInfo.maxCapacity,
        currentUsage: capacityInfo.storageUsed
      };
    } catch (error) {
      console.error('Error getting storage capacity:', error);
      return { maxStudents: 0, currentUsage: 'Error' };
    }
  }

  getCurrentMode(): string {
    return 'localStorage';
  }
}

export const storageModeManager = StorageModeManager.getInstance();
